import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quotes as quotesApi } from "@/lib/api";
import { formatMoney, statusLabel } from "@/lib/format";
import { IDS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, FileText } from "lucide-react";
import { format } from "date-fns";

const FILTERS = [
  { key: null, label: "All", testId: IDS.quotes.filterAll },
  { key: "draft", label: "Draft", testId: IDS.quotes.filterDraft },
  { key: "sent", label: "Sent", testId: IDS.quotes.filterSent },
  { key: "modification_requested", label: "Mod. Requested", testId: IDS.quotes.filterModRequested },
  { key: "accepted", label: "Accepted", testId: IDS.quotes.filterAccepted },
  { key: "declined", label: "Declined", testId: IDS.quotes.filterDeclined },
];

const StatusBadge = ({ s }) => {
  const map = {
    draft: "bg-slate-100 text-[#64748B]",
    sent: "bg-blue-50 text-[#2563EB]",
    accepted: "bg-green-50 text-[#10B981]",
    declined: "bg-red-50 text-[#EF4444]",
    modification_requested: "bg-amber-50 text-[#EA580C]",
  };
  return (
    <span className={`px-2.5 py-1 text-[11px] rounded-full uppercase tracking-[0.1em] font-semibold whitespace-nowrap ${map[s] || "bg-slate-100"}`}>
      {statusLabel(s)}
    </span>
  );
};

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = (status) => quotesApi.list(status).then(({ data }) => setItems(data || []));
  useEffect(() => { load(filter); }, [filter]);

  const filtered = items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      it.customer_name?.toLowerCase().includes(s) ||
      it.title?.toLowerCase().includes(s) ||
      it.quote_number?.toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid={IDS.quotes.listPage} className="p-5 sm:p-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">Quotes</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            All quotes
          </h1>
        </div>
        <Button
          data-testid={IDS.quotes.newBtn}
          onClick={() => nav("/quotes/new")}
          className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-5"
        >
          <Plus size={16} className="mr-2"/> New quote
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            data-testid={f.testId}
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold transition-colors duration-200 ${
              filter === f.key ? "bg-[#001F3F] text-white" : "bg-white border border-slate-200 text-[#334155] hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"/>
          <Input
            placeholder="Search customer, title, or number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 w-64 border-slate-200"
          />
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
          <div className="col-span-2">Number</div>
          <div className="col-span-4">Job</div>
          <div className="col-span-2">Customer</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        <div className="divide-y divide-slate-100">
          {filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-[#64748B] flex flex-col items-center gap-3">
              <FileText size={30} className="text-slate-300"/>
              No quotes yet. <button onClick={() => nav("/quotes/new")} className="text-[#2563EB] font-semibold hover:underline">Create your first</button>
            </div>
          )}
          {filtered.map((it) => {
            const subtotal = (it.line_items || []).reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
            const total = subtotal * (1 + (it.tax_rate || 0));
            return (
              <button
                key={it.quote_id}
                data-testid={IDS.quotes.row(it.quote_id)}
                onClick={() => nav(`/quotes/${it.quote_id}`)}
                className="w-full text-left px-5 py-4 grid grid-cols-12 gap-3 items-center hover:bg-slate-50 transition-colors duration-200"
              >
                <div className="col-span-6 md:col-span-2 text-xs font-mono text-[#334155]">{it.quote_number} · v{it.version}</div>
                <div className="col-span-12 md:col-span-4 min-w-0">
                  <div className="text-sm font-semibold text-[#001F3F] truncate">{it.title}</div>
                  <div className="text-xs text-[#64748B] md:hidden truncate">{it.customer_name}</div>
                </div>
                <div className="hidden md:block col-span-2 text-sm text-[#334155] truncate">{it.customer_name}</div>
                <div className="col-span-6 md:col-span-2"><StatusBadge s={it.status}/></div>
                <div className="col-span-6 md:col-span-2 text-right">
                  <div className="text-sm font-bold text-[#001F3F] tabular-nums">{formatMoney(total, it.currency)}</div>
                  <div className="text-[11px] text-[#64748B]">{it.created_at ? format(new Date(it.created_at), "MMM d") : ""}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
